import { ProductOption, ProductVariant, ProductVariantSelectedOption } from 'shopify-types'
import { type GrouppedOptionsByName, ProductDataForTags, ProductOptionName } from 'ui'

type Props = {
  options: ProductOption[]
  variants: ProductVariant[]
  optionNamesForSelect: ProductOptionName[]
}

type MappedVariants = {
  id: string
  selectedOptions: Record<string, string>
}[]

type TagListOptions = {
  title: string
  variants: {
    [key: string]: string
  }
}[]

const mapOptionsDataForTags = (options: ProductOption[]): ProductDataForTags[][] => {
  return options.map(({ values, name }) =>
    values.map(value => ({ title: value, variants: { [name]: value } })),
  )
}

export const mapOptionsData = (selectedOptions: ProductVariantSelectedOption[]) => {
  return Object.fromEntries(selectedOptions.map(({ name, value }) => [name, value]))
}

const generateProductTagsData = (
  productDataForTags: ProductDataForTags[][],
  existingVariants: MappedVariants,
): ProductDataForTags[] => {
  if (!productDataForTags.length) {
    return []
  }

  const [firstOptions, ...restOptions] = productDataForTags

  const productTagsData = firstOptions.flatMap(({ title: firstTitle, variants: firstVariants }) =>
    !restOptions.length
      ? [{ title: firstTitle, variants: { ...firstVariants } }]
      : generateProductTagsData(restOptions, existingVariants).flatMap(
          ({ title: restTitle, variants: restVariants }) => {
            const combinedVariants = { ...firstVariants, ...restVariants }

            // Check if this combination exists in the mapped variants
            const isValidCombination = existingVariants.some(variant =>
              Object.entries(combinedVariants).every(
                ([name, value]) => variant.selectedOptions[name] === value,
              ),
            )

            if (isValidCombination) {
              return [
                {
                  title: `${firstTitle} / ${restTitle}`,
                  variants: combinedVariants,
                },
              ]
            }

            return [] // Exclude invalid combinations
          },
        ),
  )

  return productTagsData
}

export const mapProductOptions = ({ options, variants, optionNamesForSelect }: Props) => {
  const optionsForSelect: ProductOption[] = options.filter(
    ({ name, values }) =>
      optionNamesForSelect.includes(name as ProductOptionName) && values.length > 1,
  )

  const filteredTags: ProductOption[] = options.filter(
    ({ name, values }) =>
      !optionNamesForSelect.includes(name as ProductOptionName) || values.length === 1,
  )

  const mappedVariants: MappedVariants = variants.map(({ id, selectedOptions }) => ({
    id: id?.split('/')?.at(-1) ?? id,
    selectedOptions: mapOptionsData(selectedOptions),
  }))

  const optionsForTagList: TagListOptions = generateProductTagsData(
    mapOptionsDataForTags(filteredTags),
    mappedVariants,
  )

  return {
    optionsForSelect,
    optionsForTagList,
    mappedVariants,
  }
}

export const getValidTagsForOption = (
  options: GrouppedOptionsByName,
  selectOptions: {
    id: string
    name: string
    values: string[]
  }[],
  existingVariants: MappedVariants,
): TagListOptions => {
  return Object.entries(options).flatMap(([optionName, optionTitle]) => {
    const matchingSelectOption = selectOptions.find(
      selectOption => selectOption.name === optionName,
    )

    if (!matchingSelectOption) return []

    // Filter existing variants based on selected option
    const validVariants = existingVariants.filter(variant =>
      Object.entries(variant.selectedOptions).find(
        ([name, value]) => name === optionName && value === optionTitle,
      ),
    )

    // Generate product data for tags from valid variants
    const productDataForTags = validVariants.map(variant =>
      Object.entries(variant.selectedOptions).flatMap(([name, value]) =>
        name === optionName && value === optionTitle
          ? []
          : [
              {
                title: value,
                variants: { [name]: value },
              },
            ],
      ),
    )

    return generateProductTagsData(groupProductDataForTags(productDataForTags), validVariants)
  })
}

// Helper function to group the product data by option type
const groupProductDataForTags = (
  productDataForTags: ProductDataForTags[][],
): ProductDataForTags[][] => {
  const groupedData: Record<string, ProductDataForTags[]> = {}

  productDataForTags.flat().forEach(({ variants }) => {
    const optionKey = Object.keys(variants)[0]
    if (!groupedData[optionKey]) {
      groupedData[optionKey] = []
    }
    const isDuplicate = groupedData[optionKey].some(tag => tag.title === variants[optionKey])

    if (!isDuplicate) {
      groupedData[optionKey].push({ title: variants[optionKey], variants })
    }
  })

  return Object.values(groupedData)
}
